import React from 'react';
import {Link} from 'react-router-dom';
import {AdminSortOrder, AdminUser, AdminUserSortField} from '../../domain/admin';
import {AdminBadge, NoPasswordBadge, SubscriptionBadge} from './AdminBadges';
import {EmptyState, ErrorState, LoadingState, Timestamp, formatCount, formatSeconds} from './AdminPrimitives';
import {ChevronDownIcon} from '../Icons';

interface Column {
  key: string;
  label: string;
  /** Only the fields the endpoint will actually sort by are clickable. */
  sort?: AdminUserSortField;
  align?: 'left' | 'right';
  title?: string;
}

const COLUMNS: Column[] = [
  {key: 'user', label: 'User', sort: 'email'},
  {key: 'plan', label: 'Plan'},
  {
    key: 'seconds',
    label: 'Lifetime',
    sort: 'transcription_seconds_total',
    align: 'right',
    title: 'Lifetime billable transcription time',
  },
  {key: 'transcriptions', label: 'Transcriptions', sort: 'transcription_count', align: 'right'},
  {key: 'conversions', label: 'Conversions', sort: 'conversion_count', align: 'right'},
  {key: 'lastActivity', label: 'Last transcription', sort: 'last_transcription_at'},
  {key: 'created', label: 'Joined', sort: 'created_at'},
];

interface AdminUserTableProps {
  users: AdminUser[];
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  sort: AdminUserSortField;
  order: AdminSortOrder;
  onSortChange: (sort: AdminUserSortField) => void;
  hasFilters: boolean;
}

/**
 * The user table.
 *
 * Every subscription cell badges off `effectiveSubscriptionPlan` / `isSubscriptionActive` — the
 * derived pair — and the raw Paddle mirror is not rendered here at all. A lapsed Pro reads
 * `subscription_plan: 'pro'` in the mirror while the app treats them as free, and this is the
 * screen where showing the wrong one would be believed.
 */
const AdminUserTable: React.FC<AdminUserTableProps> = ({
  users,
  isLoading,
  error,
  onRetry,
  sort,
  order,
  onSortChange,
  hasFilters,
}) => {
  if (error && users.length === 0) {
    return <ErrorState message={error} onRetry={onRetry} />;
  }

  if (isLoading && users.length === 0) {
    return <LoadingState message="Loading users…" />;
  }

  if (users.length === 0) {
    return (
      <EmptyState
        message={hasFilters ? 'No users match these filters.' : 'No users yet.'}
        hint={hasFilters ? 'Clear the filters to see everyone.' : undefined}
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[900px] text-left text-xs">
        <thead>
          <tr className="border-b border-gray-800 bg-gray-950/40 text-[11px] uppercase tracking-wide text-gray-500">
            {COLUMNS.map(column => (
              <th
                key={column.key}
                scope="col"
                title={column.title}
                className={`px-4 py-2 font-medium ${column.align === 'right' ? 'text-right' : ''}`}
              >
                {column.sort ? (
                  <button
                    type="button"
                    onClick={() => onSortChange(column.sort as AdminUserSortField)}
                    className={`inline-flex items-center gap-1 transition-colors hover:text-gray-200
                     cursor-pointer ${sort === column.sort ? 'text-gray-200' : ''}`}
                  >
                    {column.label}
                    {sort === column.sort && (
                      <ChevronDownIcon
                        className={`h-3 w-3 ${order === 'asc' ? 'rotate-180' : ''}`}
                      />
                    )}
                  </button>
                ) : (
                  column.label
                )}
              </th>
            ))}
            <th scope="col" className="px-4 py-2" />
          </tr>
        </thead>

        <tbody className="divide-y divide-gray-800/60">
          {users.map(user => (
            <tr key={user.id} className="transition-colors hover:bg-gray-900/60">
              <td className="max-w-[280px] px-4 py-2.5">
                <Link to={`/admin/users/${user.id}`} className="block min-w-0">
                  <span className="block truncate text-gray-100 hover:text-indigo-300">
                    {user.name || user.email}
                  </span>
                  {user.name && (
                    <span className="block truncate text-[11px] text-gray-500">{user.email}</span>
                  )}
                </Link>
                <div className="mt-1 flex flex-wrap gap-1">
                  {user.isAdmin && <AdminBadge />}
                  {!user.passwordSet && <NoPasswordBadge />}
                  {(user.transcriptionsInFlight ?? 0) > 0 && (
                    <span
                      className="text-[11px] text-indigo-300"
                      title="Transcriptions running right now"
                    >
                      {user.transcriptionsInFlight} in flight
                    </span>
                  )}
                </div>
              </td>

              <td className="px-4 py-2.5">
                <SubscriptionBadge
                  effectivePlan={user.effectiveSubscriptionPlan}
                  isActive={user.isSubscriptionActive}
                  rawPlan={user.subscriptionPlan}
                />
              </td>

              <td className="px-4 py-2.5 text-right tabular-nums text-gray-200">
                {formatSeconds(user.transcriptionSecondsTotal)}
              </td>

              <td className="px-4 py-2.5 text-right tabular-nums text-gray-300">
                {formatCount(user.transcriptionCount)}
                {(user.transcriptionsFailed ?? 0) > 0 && (
                  <span className="ml-1 text-[11px] text-red-300" title="Failed transcriptions">
                    ({user.transcriptionsFailed} failed)
                  </span>
                )}
              </td>

              <td className="px-4 py-2.5 text-right tabular-nums text-gray-300">
                {formatCount(user.conversionCount)}
              </td>

              <td className="px-4 py-2.5 text-gray-400">
                <Timestamp iso={user.lastTranscriptionAt} fallback="Never" />
              </td>

              <td className="px-4 py-2.5 text-gray-400">
                <Timestamp iso={user.createdAt} />
              </td>

              <td className="px-4 py-2.5 text-right">
                <Link
                  to={`/admin/users/${user.id}`}
                  className="text-[11px] text-indigo-400 hover:text-indigo-300"
                >
                  Open
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AdminUserTable;
